import React, { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as CryptoBackIcon } from '@pkg/assets/icons/v2/crypto_back.svg';
import { ReactComponent as CryptoBTCIcon } from '@pkg/assets/icons/v2/crypto_btc.svg';
import { ReactComponent as CryptoETHIcon } from '@pkg/assets/icons/v2/crypto_eth.svg';
import { ReactComponent as CryptoUSDCIcon } from '@pkg/assets/icons/v2/crypto_usdc.svg';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { CryptoType } from '@pkg/types/enums';

import { type CheckoutMethodV2 } from '@app/client/common/structs/orders/cart';
import { useCallCheckout } from '@app/client/resources/cart';
import { setCryptoPaymentData } from '@app/store/actions/crypto';
import { addNotification } from '@app/store/actions/notification';

interface CryptoStepOptionProps {
    onBack: () => void;
    onSelectCryptoType: (type: CryptoType) => void;
}

export const CryptoStepOption = ({ onBack, onSelectCryptoType }: CryptoStepOptionProps) => {
    const { t } = useTranslation(['yat_picker']);
    const dispatch = useAppDispatch();
    const { orderId, orderItems } = useAppSelector((rootState) => rootState.picker);
    const [initialized, setInitialized] = useState(false);
    const [backButtonDisabled, setBackButtonDisabled] = useState(true);
    const [methodSwitchProcessing, setMethodSwitchProcessing] = useState(false);

    const { mutateAsync: checkout } = useCallCheckout();

    const totalPrice = useMemo(() => {
        return orderItems
            .filter((orderItem) => !orderItem.marked_invalid_at)
            .map((orderItem) => orderItem.unit_price_in_cents / 100)
            .reduce((a, b) => a + b, 0);
    }, [orderItems]);

    useEffect(() => {
        if (initialized) {
            return;
        }
        const checkoutMethod: CheckoutMethodV2 = {
            method: 'CoinbaseCommerce',
            amount: totalPrice * 100,
        };
        setMethodSwitchProcessing(true);
        checkout({ value: checkoutMethod, id: orderId })
            .then((r) => {
                if (r.ok) {
                    const methods = r.value.payment_method_data.methods;
                    dispatch(setCryptoPaymentData(methods));
                    setInitialized(true);
                }
                if (!r.ok && r.error?.detail) {
                    dispatch(addNotification({ message: r.error.detail, severity: 'error' }));
                }
            })
            .catch((e) => {
                dispatch(addNotification({ message: e || t('checkout_failed'), severity: 'error' }));
            })
            .finally(() => {
                setMethodSwitchProcessing(false);
            });
    }, [checkout, dispatch, initialized, orderId, t, totalPrice]);

    useEffect(() => {
        //adding this further delay on the buttons so users can't switch between each payment method too quickly
        setTimeout(() => {
            setBackButtonDisabled(!methodSwitchProcessing && !initialized);
        }, 1000);
    }, [methodSwitchProcessing, initialized]);

    const backButtonDisabledClass = useMemo(() => {
        if (backButtonDisabled) {
            return 'yat-picker-checkout__sidebar-back-disabled';
        }
    }, [backButtonDisabled]);

    return (
        <div className="yat-picker-checkout__sidebar-vertical full">
            <button
                className={`yat-picker-checkout__sidebar-back ${backButtonDisabledClass}`}
                aria-label={t('common:back_to_previous_page')}
                onClick={onBack}
            >
                <CryptoBackIcon />
            </button>
            <h2
                className="yat-picker-checkout__sidebar-cryptotitle"
                dangerouslySetInnerHTML={{ __html: t('pay_crypto_title') }}
            />
            <p className="yat-picker-checkout__sidebar-cryptoinstruct">{t('pay_crypto_instruction')}</p>
            <div>
                <DepButton
                    size="large"
                    display={DepButtonDisplay.block}
                    classes="yat-picker-checkout__sidebar-crypto pay"
                    onClick={() => onSelectCryptoType(CryptoType.BTC)}
                    disabled={!initialized}
                >
                    <CryptoBTCIcon />
                    <span>{t('pay_btc')}</span>
                </DepButton>
                <DepButton
                    size="large"
                    display={DepButtonDisplay.block}
                    classes="yat-picker-checkout__sidebar-crypto pay"
                    onClick={() => onSelectCryptoType(CryptoType.ETH)}
                    disabled={!initialized}
                >
                    <CryptoETHIcon />
                    <span>{t('pay_eth')}</span>
                </DepButton>
                <DepButton
                    size="large"
                    display={DepButtonDisplay.block}
                    classes="yat-picker-checkout__sidebar-crypto pay"
                    onClick={() => onSelectCryptoType(CryptoType.USDC)}
                    disabled={!initialized}
                >
                    <CryptoUSDCIcon />
                    <span>{t('pay_usdc')}</span>
                </DepButton>
            </div>

            <p className="yat-picker-checkout__sidebar-cryptoinstruct sub-copy">{t('pay_crypto_current_support')}</p>
            <div className="yat-picker-checkout__sidebar-alternative">
                <span onClick={onBack}>{t('pay_crypto_alternative')}</span>
            </div>
        </div>
    );
};
